import React from "react";
import { Helmet } from "react-helmet-async";

export const QFLOW_SITE_NAME = "QFlow.ai";
export const QFLOW_SITE_URL = "https://qflow.ai";
export const QFLOW_DEFAULT_SOCIAL_IMAGE = `${QFLOW_SITE_URL}/images/qflow-social.png`;

export function resolveMarketingUrl(pathOrUrl) {
  if (!pathOrUrl || pathOrUrl === "/") {
    return QFLOW_SITE_URL;
  }

  try {
    const resolvedUrl = new URL(pathOrUrl, `${QFLOW_SITE_URL}/`).toString();
    return resolvedUrl.endsWith("/") && resolvedUrl !== `${QFLOW_SITE_URL}/`
      ? resolvedUrl.slice(0, -1)
      : resolvedUrl;
  } catch (error) {
    return QFLOW_SITE_URL;
  }
}

export function formatMarketingPageTitle(pageTitle, brandFirst = false) {
  return brandFirst
    ? `${QFLOW_SITE_NAME} | ${pageTitle}`
    : `${pageTitle} | ${QFLOW_SITE_NAME}`;
}

export function SiteMetadataDefaults() {
  return (
    <Helmet defaultTitle={QFLOW_SITE_NAME}>
      <meta charSet="utf-8" />
      <meta name="referrer" content="same-origin" />
      <meta name="author" content={QFLOW_SITE_NAME} />
      <meta name="theme-color" content="#ffffff" />
      <meta name="Content-Type" content="text/html; charset=UTF-8" />
      <link rel="apple-touch-icon" href="/logo192.png" />
      <meta name="twitter:site" content="@qflowai" />
      <meta property="og:site_name" content={QFLOW_SITE_NAME} />
    </Helmet>
  );
}

export default function MarketingPageMetadata({
  pageTitle,
  description,
  canonicalPath,
  brandFirst = false,
  socialTitle,
  socialDescription,
  socialImage = QFLOW_DEFAULT_SOCIAL_IMAGE,
  socialImageAlt = QFLOW_SITE_NAME,
  type = "website",
  robots,
  jsonLd,
}) {
  const title = formatMarketingPageTitle(pageTitle, brandFirst);
  const canonicalUrl = resolveMarketingUrl(canonicalPath);
  const socialImageUrl = resolveMarketingUrl(socialImage);
  const resolvedSocialTitle = socialTitle || title;
  const resolvedSocialDescription = socialDescription || description;
  const robotsContent = robots || (process.env.REACT_APP_STAGE === "dev" ? "noindex" : "index");
  const jsonLdEntries = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-us" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={QFLOW_SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={resolvedSocialTitle} />
      <meta property="og:description" content={resolvedSocialDescription} />
      <meta property="og:image" content={socialImageUrl} />
      <meta property="og:image:alt" content={socialImageAlt} />

      <meta name="twitter:site" content="@qflowai" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedSocialTitle} />
      <meta name="twitter:description" content={resolvedSocialDescription} />
      <meta name="twitter:image" content={socialImageUrl} />
      <meta name="twitter:image:alt" content={socialImageAlt} />

      {jsonLdEntries.map((entry, index) => (
        <script key={`${entry["@type"] || "structured-data"}-${index}`} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
